import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import axios from "axios";
import { 
  AlertTriangle, TrendingUp, TrendingDown, Activity, RefreshCw, 
  CheckCircle, Clock, Bell, BarChart3, Loader2, Info
} from "lucide-react";

const AnomalyDetectionPage = () => {
  const { sessionToken } = useAuth();
  const [anomalies, setAnomalies] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [viewingTenantId, setViewingTenantId] = useState(sessionStorage.getItem("viewing_tenant_id"));

  // Listen for tenant changes
  useEffect(() => {
    const handleTenantChange = () => {
      const newTenantId = sessionStorage.getItem("viewing_tenant_id");
      setViewingTenantId(newTenantId);
    };
    
    window.addEventListener("tenant-changed", handleTenantChange);
    window.addEventListener("storage", handleTenantChange);
    
    // Also check on mount in case it's set
    const currentTenantId = sessionStorage.getItem("viewing_tenant_id");
    if (currentTenantId && currentTenantId !== viewingTenantId) {
      setViewingTenantId(currentTenantId);
    }
    
    return () => {
      window.removeEventListener("tenant-changed", handleTenantChange);
      window.removeEventListener("storage", handleTenantChange);
    };
  }, []);

  const hasTenant = !!viewingTenantId;

  useEffect(() => {
    if (hasTenant && sessionToken) {
      fetchData();
    }
  }, [viewingTenantId, sessionToken]);

  const fetchData = async () => {
    if (!viewingTenantId || !sessionToken) return;
    
    setIsLoading(true);
    try {
      const authParam = `Bearer ${sessionToken}`;
      const [anomaliesRes, baselinesRes] = await Promise.all([
        axios.get(`${API}/ai-analytics/anomalies`, {
          params: { "X-Tenant-ID": viewingTenantId, "Authorization": authParam }
        }),
        axios.get(`${API}/ai-analytics/baselines`, {
          params: { "X-Tenant-ID": viewingTenantId, "Authorization": authParam }
        })
      ]);
      
      setAnomalies(anomaliesRes.data.anomalies || []);
      setBaselines(baselinesRes.data.baselines || []);
      setTenantName(anomaliesRes.data.tenant_name || baselinesRes.data.tenant_name || "");
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load anomaly data");
    } finally {
      setIsLoading(false);
    }
  };

  const calculateBaselines = async () => {
    setIsCalculating(true);
    try {
      const authParam = `Bearer ${sessionToken}`;
      await axios.post(`${API}/ai-analytics/baselines/calculate`, null, {
        params: { "X-Tenant-ID": viewingTenantId, "Authorization": authParam }
      });
      toast.success("Baseline calculation started");
      // Refresh data after a short delay
      setTimeout(fetchData, 3000);
    } catch (error) {
      console.error("Error calculating baselines:", error);
      toast.error("Failed to calculate baselines");
    } finally {
      setIsCalculating(false);
    }
  };

  const acknowledgeAnomaly = async (anomalyId) => {
    try {
      const authParam = `Bearer ${sessionToken}`;
      await axios.patch(`${API}/ai-analytics/anomalies/${anomalyId}`, null, {
        params: { "X-Tenant-ID": viewingTenantId, "Authorization": authParam, status: "acknowledged" }
      });
      toast.success("Anomaly acknowledged");
      fetchData();
    } catch (error) {
      console.error("Error acknowledging anomaly:", error);
      toast.error("Failed to acknowledge anomaly");
    }
  };

  const resolveAnomaly = async (anomalyId) => {
    try {
      const authParam = `Bearer ${sessionToken}`;
      await axios.patch(`${API}/ai-analytics/anomalies/${anomalyId}`, null, {
        params: { "X-Tenant-ID": viewingTenantId, "Authorization": authParam, status: "resolved" }
      });
      toast.success("Anomaly resolved");
      fetchData();
    } catch (error) {
      console.error("Error resolving anomaly:", error);
      toast.error("Failed to resolve anomaly");
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical": return "bg-red-100 text-red-800 border-red-300";
      case "high": return "bg-orange-100 text-orange-800 border-orange-300";
      case "medium": return "bg-yellow-100 text-yellow-800 border-yellow-300";
      default: return "bg-blue-100 text-blue-800 border-blue-300";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new": return "bg-red-500";
      case "acknowledged": return "bg-yellow-500";
      case "resolved": return "bg-green-500";
      default: return "bg-slate-500";
    }
  };

  const getDirectionIcon = (direction) => {
    return direction === "up" ? TrendingUp : TrendingDown;
  };

  // Show message if no tenant is set
  if (!hasTenant) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-amber-800">
              <AlertTriangle className="w-6 h-6" />
              <div>
                <h3 className="font-semibold">No Tenant Selected</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Anomaly Detection requires a tenant context to function. Please select a tenant 
                  from the sidebar or ensure you are logged into an account with tenant access.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00A5A8]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="anomaly-detection-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#00A5A8]" />
            Anomaly Detection
          </h1>
          <p className="text-slate-600 mt-1">
            Monitor unusual patterns in {tenantName || "your"} marketing data
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            onClick={calculateBaselines}
            disabled={isCalculating}
            className="bg-[#00A5A8] hover:bg-[#008B8E]"
          >
            {isCalculating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <BarChart3 className="w-4 h-4 mr-2" />
            )}
            Calculate Baselines
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">New Anomalies</p>
                <p className="text-2xl font-bold text-red-600">
                  {anomalies.filter(a => a.status === "new").length}
                </p>
              </div>
              <Bell className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Acknowledged</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {anomalies.filter(a => a.status === "acknowledged").length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Resolved</p>
                <p className="text-2xl font-bold text-green-600">
                  {anomalies.filter(a => a.status === "resolved").length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Baselines Set</p>
                <p className="text-2xl font-bold text-[#00A5A8]">
                  {baselines.length}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-[#00A5A8]/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Anomalies List */}
      <Card>
        <CardHeader>
          <CardTitle>Detected Anomalies</CardTitle>
          <CardDescription>
            Unusual patterns detected in your marketing metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {anomalies.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No anomalies detected</p>
              <p className="text-sm mt-1">
                Click the Calculate Baselines button to set up anomaly detection for your metrics
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {anomalies.map((anomaly, idx) => {
                const DirectionIcon = getDirectionIcon(anomaly.direction);
                return (
                  <div
                    key={anomaly.anomaly_id || idx}
                    className={`p-4 rounded-lg border ${getSeverityColor(anomaly.severity)}`}
                    data-testid={`anomaly-${anomaly.anomaly_id || idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-3 h-3 rounded-full mt-1.5 ${getStatusColor(anomaly.status)}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold capitalize">
                              {anomaly.metric?.replace(/_/g, " ")}
                            </span>
                            <Badge variant="outline" className="text-xs capitalize">
                              {anomaly.type?.replace(/_/g, " ")}
                            </Badge>
                            <DirectionIcon className={`w-4 h-4 ${
                              anomaly.direction === "up" ? "text-green-600" : "text-red-600"
                            }`} />
                          </div>
                          
                          <p className="text-sm mt-1">
                            {anomaly.deviation_percent > 0 ? "+" : ""}
                            {anomaly.deviation_percent}% from expected
                            {anomaly.expected_value && anomaly.actual_value && (
                              <span className="text-slate-600 ml-2">
                                (Expected: {anomaly.expected_value.toLocaleString()}, 
                                Actual: {anomaly.actual_value.toLocaleString()})
                              </span>
                            )}
                          </p>
                          
                          {anomaly.insight && (
                            <p className="text-sm mt-2 text-slate-700">
                              {anomaly.insight}
                            </p>
                          )}
                          
                          <p className="text-xs text-slate-500 mt-2">
                            Detected: {new Date(anomaly.detected_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {anomaly.status === "new" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeAnomaly(anomaly.anomaly_id)}
                          >
                            Acknowledge
                          </Button>
                        )}
                        {anomaly.status !== "resolved" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resolveAnomaly(anomaly.anomaly_id)}
                          >
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Baselines Info */}
      <Card>
        <CardHeader>
          <CardTitle>Active Baselines</CardTitle>
          <CardDescription>
            Rolling averages used to detect anomalies
          </CardDescription>
        </CardHeader>
        <CardContent>
          {baselines.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No baselines calculated yet</p>
              <p className="text-xs mt-1">Click the Calculate Baselines button above to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {baselines.map((baseline, idx) => (
                <div
                  key={baseline.metric || idx}
                  className="p-4 rounded-lg bg-slate-50 border"
                >
                  <p className="font-medium capitalize">
                    {baseline.metric?.replace(/_/g, " ")}
                  </p>
                  <div className="mt-2 space-y-1 text-sm text-slate-600">
                    <p>Mean: {baseline.mean?.toLocaleString()}</p>
                    <p>Std Dev: {baseline.std_dev?.toFixed(2)}</p>
                    <p>Window: {baseline.window_days} days</p>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Updated: {baseline.updated_at ? new Date(baseline.updated_at).toLocaleDateString() : "N/A"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Footer */}
      <div className="flex items-start gap-2 text-xs text-slate-500 p-4 bg-slate-50 rounded-lg">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">How Anomaly Detection Works</p>
          <p className="mt-1">
            Baselines are calculated using rolling averages. When a metric deviates significantly 
            (2+ standard deviations), an anomaly is flagged. Different channels use different time windows:
            Organic Search uses 90 days, Social uses 14 days, and Paid uses 7 days.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnomalyDetectionPage;
